import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useQuery from 'hooks/useQuery';
import api from 'services/api';
import { Button, Input, Modal, Select, Text } from 'v3/components';
import { fetchVehiclesOrWagons } from 'utils/fetches';

function safeStr(v) {
  if (v === null || v === undefined) return '';
  return String(v);
}

function parseGroupIdsFromQuery(value) {
  const raw = safeStr(value).trim();
  if (!raw) return [];
  return raw
    .split(',')
    .map((x) => safeStr(x).trim())
    .filter(Boolean);
}

function buildGroupIdsQuery(groupIds) {
  const arr = Array.isArray(groupIds) ? groupIds : [];
  const cleaned = arr.map((x) => safeStr(x).trim()).filter(Boolean);
  return cleaned.length ? cleaned.join(',') : '';
}

function sanitizeIdSearch(value) {
  return safeStr(value).replace(/\D+/g, '');
}

const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const optsFromValues = (values, options) => options.filter((o) => values.includes(String(o.value)));
const valuesFromOpts = (opt) =>
  Array.isArray(opt) ? opt.map((o) => String(o.value)) : opt ? [String(opt.value)] : [];

export default function Filter({
  loading = false,
  onFilterChange,
  showAdvancedFilter = false,
  onCloseAdvanceFilterModal,
  setParentFilter,
  setAdvancedFiltersQtd,
  activeTab = 'pending',
}) {
  const navigate = useNavigate();
  const query = useQuery();
  const isLinkTab = activeTab === 'external' || activeTab === 'link';

  const [groupsOptions, setGroupsOptions] = useState([]);
  const [filter, setFilter] = useState({
    q: '',
    groupIds: [],
    linkStatus: ['all'],
    pendingStatus: 'active',
  });
  const [advancedFilters, setAdvancedFilters] = useState({
    plates: [],
    answersSearch: '',
    idSearch: null,
  });

  const lastAppliedQRef = useRef('');
  const lastAppliedAdvancedRef = useRef({ plates: [], answersSearch: '', idSearch: null });

  const LINK_STATUS_OPTIONS = useMemo(
    () => [
      { value: 'all', label: 'Todos' },
      { value: 'active', label: 'Ativos' },
      { value: 'expired', label: 'Expirados' },
    ],
    [],
  );

  const PENDING_STATUS_OPTIONS = useMemo(
    () => [
      { value: 'active', label: 'Ativos' },
      { value: 'annulled', label: 'Anulados' },
      { value: 'all', label: 'Todos' },
    ],
    [],
  );

  function normalizeLinkStatusValues(values) {
    const arr = Array.isArray(values) ? values : values ? [values] : [];
    const cleaned = arr.map(String).filter(Boolean);
    if (cleaned.includes('all') && cleaned.length > 1) {
      return cleaned.filter((v) => v !== 'all');
    }
    if (cleaned.includes('all')) return ['all'];
    return cleaned;
  }

  const GROUP_OPTIONS = useMemo(() => {
    return (groupsOptions || []).map((g) => ({
      value: String(g.id),
      label: g.name,
      raw: g,
    }));
  }, [groupsOptions]);

  async function getChecklistGroups() {
    try {
      const res = await api.get('groups', { params: { onlyActive: true } });
      const raw = res?.data?.data || res?.data?.groups || res?.data || [];
      setGroupsOptions(Array.isArray(raw) ? raw : []);
    } catch (e) {
      setGroupsOptions([]);
    }
  }

  useEffect(() => {
    getChecklistGroups();
  }, []);

  useEffect(() => {
    if (!setAdvancedFiltersQtd) return;
    const values = [
      advancedFilters.plates && advancedFilters.plates.length > 0 ? 'plates' : null,
      advancedFilters.answersSearch,
      advancedFilters.idSearch,
    ].filter((value) => value && String(value || '').trim().length > 0);
    setAdvancedFiltersQtd(values.length);
  }, [advancedFilters, setAdvancedFiltersQtd]);

  function emit(next) {
    if (!onFilterChange) return;
    const platesObjects = Array.isArray(advancedFilters.plates) ? advancedFilters.plates : [];
    const normalizedIdSearch = sanitizeIdSearch(advancedFilters.idSearch);
    onFilterChange({
      q: (next.q || '').trim(),
      groupIds: Array.isArray(next.groupIds) ? next.groupIds : [],
      plates: platesObjects,
      answersSearch: (advancedFilters.answersSearch || '').trim(),
      idSearch: normalizedIdSearch || null,
      linkStatus: normalizeLinkStatusValues(next.linkStatus),
      pendingStatus: next.pendingStatus || 'active',
    });
  }

  function getInputValue(arg) {
    if (arg === null || arg === undefined) return '';
    if (typeof arg === 'string' || typeof arg === 'number') return String(arg);
    if (arg?.target && 'value' in arg.target) return String(arg.target.value ?? '');
    if (arg && 'value' in arg) return String(arg.value ?? '');
    return String(arg ?? '');
  }

  function updateQueryString(next) {
    const qTrimmed = (next.q || '').trim();
    const groupIds = toArray(next.groupIds).map(String).filter(Boolean);

    if (!qTrimmed) query.delete('q');
    else query.set('q', qTrimmed);

    const groupCsv = buildGroupIdsQuery(groupIds);
    if (!groupCsv) query.delete('groupIds');
    else query.set('groupIds', groupCsv);

    navigate({ search: query.toString() });
  }

  function applyFilters(next) {
    const platesObjects = Array.isArray(advancedFilters.plates) ? advancedFilters.plates : [];
    const normalizedIdSearch = sanitizeIdSearch(advancedFilters.idSearch);
    const payload = {
      q: (next.q || '').trim(),
      groupIds: Array.isArray(next.groupIds) ? next.groupIds : [],
      plates: platesObjects,
      answersSearch: (advancedFilters.answersSearch || '').trim(),
      idSearch: normalizedIdSearch || null,
      linkStatus: normalizeLinkStatusValues(next.linkStatus),
      pendingStatus: next.pendingStatus || 'active',
    };

    updateQueryString(payload);
    emit(payload);
    lastAppliedQRef.current = payload.q;
    lastAppliedAdvancedRef.current = {
      plates: platesObjects,
      answersSearch: payload.answersSearch,
      idSearch: payload.idSearch,
    };

    if (onCloseAdvanceFilterModal) {
      onCloseAdvanceFilterModal();
    }
  }

  useEffect(() => {
    const q = safeStr(query.get('q')).trim();
    const groupIds = parseGroupIdsFromQuery(query.get('groupIds'));
    setFilter({ q, groupIds, linkStatus: ['all'], pendingStatus: 'active' });
    setAdvancedFilters(lastAppliedAdvancedRef.current);

    lastAppliedQRef.current = q;
  }, []);

  const groupValues = useMemo(() => toArray(filter.groupIds).map(String), [filter.groupIds]);

  const formatVehicleOptionLabel = (option) => {
    if (!option) return null;

    if (option?.type === 'wagon') {
      return `${option.plate} - Implemento`;
    } else {
      return `${option.plate} - Veículo`;
    }
  };

  return (
    <>
      <Modal
        show={showAdvancedFilter}
        handleClose={() => {
          setAdvancedFilters(lastAppliedAdvancedRef.current);

          if (setParentFilter) {
            setParentFilter((old) => ({
              ...old,
              plates: lastAppliedAdvancedRef.current.plates,
              answersSearch: lastAppliedAdvancedRef.current.answersSearch,
              idSearch: lastAppliedAdvancedRef.current.idSearch,
            }));
          }
          if (onCloseAdvanceFilterModal) onCloseAdvanceFilterModal();
        }}
        heading="Filtro Avançado"
        body={
          <Row>
            <Col md={6} sm={12} className="my-1">
              <Input
                label="Busca nas respostas"
                placeholder="Digite um termo que contenha nas respostas"
                value={advancedFilters.answersSearch}
                onChange={(e) =>
                  setAdvancedFilters((old) => ({ ...old, answersSearch: getInputValue(e) }))
                }
                disabled={loading}
              />
            </Col>
            <Col md={6} sm={12} className="my-1">
              <Input
                label="Busca por ID"
                type="number"
                placeholder="Digite o identificador do checklist"
                value={advancedFilters.idSearch}
                onChange={(e) =>
                  setAdvancedFilters((old) => ({
                    ...old,
                    idSearch: sanitizeIdSearch(getInputValue(e)),
                  }))
                }
                disabled={loading}
              />
            </Col>
            <Col md={6} sm={12} className="my-1">
              <Select.Async
                label="placa"
                placeholder="Selecione as placas"
                multiple
                horizontal
                modalHeading="placas"
                modalBodyTitle="Selecione uma placa"
                onSearch={(search) => fetchVehiclesOrWagons(search)}
                getOptionLabel={(option) => formatVehicleOptionLabel(option)}
                getOptionValue={(option) => option.id}
                value={advancedFilters.plates || []}
                onChange={(value) =>
                  setAdvancedFilters((old) => ({
                    ...old,
                    plates: value,
                  }))
                }
              />
            </Col>
          </Row>
        }
        footer={
          <Row style={{ justifyContent: 'flex-end' }}>
            <Button variant="primary" disabled={loading} onClick={() => applyFilters(filter)}>
              <Text color="white" weight={500}>
                Filtrar
              </Text>
            </Button>
          </Row>
        }
      />

      <Row className="filter">
        <Col md={isLinkTab || activeTab === 'pending' ? 4 : 6} sm={12} className="my-1">
          <Input
            label="Nome"
            labelColor="#fff"
            placeholder="Buscar por título ou responsável..."
            value={filter.q}
            disabled={loading}
            onChange={(e) => {
              const v = getInputValue(e);

              setFilter((old) => {
                const next = { ...old, q: v };
                const nextTrim = (v || '').trim();
                const hadApplied = (lastAppliedQRef.current || '').trim().length > 0;

                if (hadApplied && nextTrim.length === 0) {
                  applyFilters(next);
                }

                return next;
              });
            }}
            onKeyDown={(e) => {
              if (e?.key === 'Enter') {
                e.preventDefault();
                applyFilters(filter);
              }
            }}
          />
        </Col>
        <Col md={isLinkTab || activeTab === 'pending' ? 3 : 4} sm={12} className="my-1">
          <Select.Async
            label="Grupo"
            labelColor="#fff"
            labelWhiteSpace="nowrap"
            modalHeading="Grupos"
            modalBodyTitle="Selecione um ou mais grupos"
            multiple
            useMenuPortal
            options={GROUP_OPTIONS}
            defaultOptions={GROUP_OPTIONS}
            onSearch={async (search = '') => {
              if (!search) return GROUP_OPTIONS;
              const s = String(search).toLowerCase();
              return GROUP_OPTIONS.filter((o) =>
                String(o.label || '')
                  .toLowerCase()
                  .includes(s),
              );
            }}
            value={optsFromValues(groupValues, GROUP_OPTIONS)}
            onChange={(opt) => {
              const values = valuesFromOpts(opt);
              setFilter((old) => ({ ...old, groupIds: values })); // não aplica automático
            }}
            getOptionLabel={(opt) => opt?.label}
            getOptionValue={(opt) => opt?.value}
            placeholder="Selecione..."
            selectValuesContainerMaxHeight="44px"
            selectValuesContainerOverflowY="auto"
          />
        </Col>

        {isLinkTab ? (
          <Col md={3} sm={12} className="my-1">
            <Select.Async
              label="Status"
              labelColor="#fff"
              labelWhiteSpace="nowrap"
              modalHeading="Status"
              modalBodyTitle="Selecione um ou mais status"
              multiple
              useMenuPortal
              options={LINK_STATUS_OPTIONS}
              defaultOptions={LINK_STATUS_OPTIONS}
              onSearch={async (search = '') => {
                if (!search) return LINK_STATUS_OPTIONS;
                const s = String(search).toLowerCase();
                return LINK_STATUS_OPTIONS.filter((o) =>
                  String(o.label || '')
                    .toLowerCase()
                    .includes(s),
                );
              }}
              value={optsFromValues(
                normalizeLinkStatusValues(filter.linkStatus),
                LINK_STATUS_OPTIONS,
              )}
              onChange={(opt) => {
                const values = valuesFromOpts(opt);
                const nextValues = normalizeLinkStatusValues(values);
                setFilter((old) => ({ ...old, linkStatus: nextValues }));
              }}
              getOptionLabel={(opt) => opt?.label}
              getOptionValue={(opt) => opt?.value}
              placeholder="Selecione..."
              selectValuesContainerMaxHeight="44px"
              selectValuesContainerOverflowY="auto"
            />
          </Col>
        ) : null}

        {activeTab === 'pending' ? (
          <Col md={3} sm={12} className="my-1">
            <Select
              label="Status"
              labelTextColor="#fff"
              placeholder="Ativos"
              useMenuPortal
              options={PENDING_STATUS_OPTIONS}
              value={
                PENDING_STATUS_OPTIONS.find(
                  (opt) => opt.value === (filter.pendingStatus || 'active'),
                ) || PENDING_STATUS_OPTIONS[0]
              }
              onChange={(opt) => {
                const nextValue = opt?.value || 'active';
                setFilter((old) => ({ ...old, pendingStatus: nextValue }));
              }}
              disableSearch
              disableClear
            />
          </Col>
        ) : null}

        <Col md={2} sm={12} className="my-1" style={{ display: 'flex', alignItems: 'end' }}>
          <Button
            style={{ flex: 1 }}
            variant="success"
            disabled={loading}
            onClick={() => applyFilters(filter)}
          >
            <Text type="regular" weight={500}>
              Filtrar
            </Text>
          </Button>
        </Col>
      </Row>
    </>
  );
}
